/**
 * usePresence
 *
 * Maintains a live list of online users by consuming 'presence' messages
 * from the WebSocket layer. Does not manage the WS connection itself —
 * just listens to the message dispatcher.
 */

import { useCallback, useState } from 'react';
import type { WsMessage } from './useWebSocket';

export interface OnlineUser {
  id: string;
  username: string;
  display_name: string;
  avatar_url?: string;
}

export function usePresence(currentUserId: string) {
  const [onlineUsers, setOnlineUsers] = useState<OnlineUser[]>([]);

  /**
   * Feed this function into your useWebSocket onMessage handler.
   * It filters for `type === 'presence'` and updates state.
   */
  const handleMessage = useCallback(
    (msg: WsMessage) => {
      if (msg.type !== 'presence') return;

      const users = (msg.users as OnlineUser[]) ?? [];
      // Exclude self from the list shown to the user (optional — remove if you want to see self)
      setOnlineUsers(users.filter((u) => u.id !== currentUserId));
    },
    [currentUserId]
  );

  /** True if a specific userId is currently online */
  const isOnline = useCallback(
    (userId: string) => onlineUsers.some((u) => u.id === userId),
    [onlineUsers]
  );

  return { onlineUsers, isOnline, handleMessage };
}
